import { motion } from "motion/react";
import { Skeleton } from "./ui/skeleton";

interface LoadingMoreSkeletonProps {
  /** Quantidade de linhas de skeleton. */
  count?: number;
  /** Cor de fundo da seção (atrás dos cards). */
  sectionColor?: string;
  /** Cor de fundo de cada card. */
  rowColor?: string;
}

/**
 * Skeleton exibido durante o scroll infinito ("loading more").
 *
 * Replica a estrutura visual do OrderListItem (bolinha + nome, categoria,
 * meta e badge) para uma transição fluida — o conteúdo real entra no lugar
 * sem salto de layout. As cores acompanham a última seção visível.
 */
export function LoadingMoreSkeleton({
  count = 3,
  sectionColor = "#F0F2F5",
  rowColor = "#F9FAFB",
}: LoadingMoreSkeletonProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      aria-hidden="true"
      style={{
        backgroundColor: sectionColor,
        paddingLeft: "16px",
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          style={{
            backgroundColor: rowColor,
            borderTop: "1px solid var(--neutral-200)",
            borderLeft: "1px solid var(--neutral-200)",
            borderRight: "1px solid var(--neutral-200)",
            paddingLeft: "16px",
            paddingRight: "16px",
            paddingTop: "12px",
            paddingBottom: "12px",
          }}
        >
          {/* Bolinha + Nome */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "4px",
            }}
          >
            <Skeleton
              delay={index * 60}
              style={{ width: "9px", height: "9px", borderRadius: "50%" }}
            />
            <Skeleton
              delay={index * 60 + 20}
              style={{
                height: "16px",
                width: `${130 + index * 10}px`,
                borderRadius: "4px",
              }}
            />
          </div>

          {/* Categoria */}
          <div style={{ marginLeft: "17px", marginBottom: "8px" }}>
            <Skeleton
              delay={index * 60 + 40}
              style={{
                height: "14px",
                width: `${100 + index * 8}px`,
                borderRadius: "4px",
              }}
            />
          </div>

          {/* Meta (distância / bairro) */}
          <div style={{ marginLeft: "17px", marginBottom: "8px" }}>
            <Skeleton
              delay={index * 60 + 60}
              style={{
                height: "12px",
                width: `${90 + index * 6}px`,
                borderRadius: "4px",
              }}
            />
          </div>

          {/* Badge */}
          <div style={{ marginLeft: "17px" }}>
            <Skeleton
              delay={index * 60 + 80}
              style={{ height: "20px", width: "72px", borderRadius: "6px" }}
            />
          </div>
        </div>
      ))}
    </motion.div>
  );
}
